import type { IWorkerSettingsInput } from '../../../__generated__/graphql';
import { ServiceListingStatus } from '../../../__generated__/graphql';
import type { IProviderWorkerFieldsFragment } from '../../services/serviceService/__generated__/queries.generated';
import { PROVIDER_TAX_INFO_PATH } from './servicePaths';
import { DistanceUnit, asDistanceUnit } from '../../utils/units';
import { normalizeServiceArea } from '../../utils/postalCode';
import { distanceWord, SERVICE_TRAVEL_LIMITS } from '../../utils/serviceUnits';

/**
 * The worker settings form (spec 019 W6, W7): one model for the 7-step wizard
 * and the manage page. Pure; limits match the API's `WorkerSettingsInput`
 * (D14c buffer 0 to 6 hours, notice 1 to 72 hours, D20 travel).
 */

export type ProviderWorker = IProviderWorkerFieldsFragment;

export const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

export interface DayHours {
  available: boolean;
  start: string;
  end: string;
}
export type WeeklyHoursForm = Record<Weekday, DayHours>;

export interface SuppliesForm {
  cleaningProducts: boolean;
  tools: boolean;
  vacuum: boolean;
  linens: boolean;
  guestConsumables: boolean;
}

export const SUPPLY_LABELS: Record<keyof SuppliesForm, string> = {
  cleaningProducts: 'Cleaning products',
  tools: 'Tools (mop, brushes, cloths)',
  vacuum: 'Vacuum',
  linens: 'Bed linens and towels',
  guestConsumables: 'Guest supplies (soap, toilet paper, coffee)',
};

export interface WorkerForm {
  displayName: string;
  bio: string;
  yearsExperience: string;
  /** Comma separated. */
  languages: string;
  /**
   * Spec 028: the unit of the radius, the farthest distance and the price per
   * unit (the worker's own; a new worker's is the brand's).
   */
  distanceUnit: DistanceUnit;
  serviceRadiusMiles: string;
  /** ZIP codes separated by commas or spaces. */
  serviceZips: string;
  acceptsOutOfRadius: boolean;
  perMileBeyondRadius: string;
  maxTravelMiles: string;
  /** D25: null working hours on the API means the location's operating hours. */
  useLocationHours: boolean;
  workingHours: WeeklyHoursForm;
  bufferHours: string;
  advanceNoticeHours: string;
  instantBook: boolean;
  confirmWindowHours: string;
  supplies: SuppliesForm;
}

export type WorkerFormKey = keyof WorkerForm;
export type WorkerFormErrors = Partial<Record<WorkerFormKey, string>>;

export const DEFAULT_BUFFER_HOURS = 1;
export const DEFAULT_ADVANCE_NOTICE_HOURS = 12;
export const DEFAULT_CONFIRM_WINDOW_HOURS = 4;
export const DEFAULT_RADIUS_MILES = 15;

export function defaultWorkingHours(): WeeklyHoursForm {
  const hours = {} as WeeklyHoursForm;
  for (const day of WEEKDAYS) {
    hours[day] = { available: day !== 'sunday', start: '09:00', end: '17:00' };
  }
  return hours;
}

export function emptyWorkerForm(unit: DistanceUnit = 'mi'): WorkerForm {
  return {
    displayName: '',
    bio: '',
    yearsExperience: '',
    languages: '',
    distanceUnit: unit,
    serviceRadiusMiles: String(SERVICE_TRAVEL_LIMITS[unit].defaultRadius),
    serviceZips: '',
    acceptsOutOfRadius: false,
    perMileBeyondRadius: '',
    maxTravelMiles: '',
    useLocationHours: true,
    workingHours: defaultWorkingHours(),
    bufferHours: String(DEFAULT_BUFFER_HOURS),
    advanceNoticeHours: String(DEFAULT_ADVANCE_NOTICE_HOURS),
    instantBook: false,
    confirmWindowHours: String(DEFAULT_CONFIRM_WINDOW_HOURS),
    supplies: {
      cleaningProducts: false,
      tools: false,
      vacuum: false,
      linens: false,
      guestConsumables: false,
    },
  };
}

const str = (value: unknown) => (value === null || value === undefined ? '' : String(value));

function hoursFrom(raw: unknown): WeeklyHoursForm | null {
  if (!raw || typeof raw !== 'object') return null;
  const hours = defaultWorkingHours();
  for (const day of WEEKDAYS) {
    const entry = (raw as Record<string, any>)[day];
    if (!entry) continue;
    hours[day] = {
      available: entry.available === true,
      start: entry.alwaysAvailable ? '00:00' : String(entry.start ?? '09:00').slice(0, 5),
      end: entry.alwaysAvailable ? '23:59' : String(entry.end ?? '17:00').slice(0, 5),
    };
  }
  return hours;
}

export function workerToForm(pw: ProviderWorker): WorkerForm {
  const w = pw.worker;
  const custom = hoursFrom(pw.settings.workingHours);
  return {
    displayName: w.displayName ?? '',
    bio: w.bio ?? '',
    yearsExperience: str(w.yearsExperience),
    languages: (w.languages ?? []).join(', '),
    distanceUnit: asDistanceUnit((w as { distanceUnit?: string | null }).distanceUnit),
    serviceRadiusMiles: str(w.serviceRadiusMiles),
    serviceZips: (pw.settings.serviceZips ?? []).join(', '),
    acceptsOutOfRadius: w.acceptsOutOfRadius,
    perMileBeyondRadius: str(w.perMileBeyondRadius),
    maxTravelMiles: str(w.maxTravelMiles),
    useLocationHours: custom === null,
    workingHours: custom ?? defaultWorkingHours(),
    bufferHours: str(w.bufferHours),
    advanceNoticeHours: str(w.advanceNoticeHours),
    instantBook: w.instantBook,
    confirmWindowHours: str(pw.settings.confirmWindowHours),
    supplies: {
      cleaningProducts: w.suppliesProvided.cleaningProducts,
      tools: w.suppliesProvided.tools,
      vacuum: w.suppliesProvided.vacuum,
      linens: w.suppliesProvided.linens,
      guestConsumables: w.suppliesProvided.guestConsumables,
    },
  };
}

// ─── sections ───────────────────────────────────────────────────────────────

export type WorkerSection = 'profile' | 'area' | 'schedule' | 'supplies' | 'booking';

export const SECTION_FIELDS: Record<WorkerSection, readonly WorkerFormKey[]> = {
  profile: ['displayName', 'bio', 'yearsExperience', 'languages'],
  area: [
    'serviceRadiusMiles',
    'serviceZips',
    'acceptsOutOfRadius',
    'perMileBeyondRadius',
    'maxTravelMiles',
  ],
  schedule: ['useLocationHours', 'workingHours', 'bufferHours', 'advanceNoticeHours'],
  supplies: ['supplies'],
  booking: ['instantBook', 'confirmWindowHours'],
};

/** Whole numbers only; `null` with a message when out of range. */
function wholeNumber(
  text: string,
  min: number,
  max: number,
  message: string,
): { value: number | null; error?: string } {
  const trimmed = text.trim();
  const value = Number(trimmed);
  if (!trimmed || !Number.isInteger(value) || value < min || value > max) {
    return { value: null, error: message };
  }
  return { value };
}

/** ZIP codes from free text: 5 digits each, no repeats. */
export function parseZipList(text: string): { zips: string[]; invalid: string[] } {
  // A full Canadian code ("M4W 1A1") is one entry, not two.
  const parts = text
    .replace(/\b([A-Za-z]\d[A-Za-z])\s+(\d[A-Za-z]\d)\b/g, '$1$2')
    .split(/[\s,;]+/)
    .map(p => p.trim())
    .filter(Boolean);
  const zips: string[] = [];
  const invalid: string[] = [];
  for (const part of parts) {
    // Spec 028: a 5-digit ZIP, or a Canadian area (FSA "M5V", or a full code kept as its FSA).
    const area = normalizeServiceArea('US', part) ?? normalizeServiceArea('CA', part);
    if (!area) invalid.push(part);
    else if (!zips.includes(area)) zips.push(area);
  }
  return { zips, invalid };
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The API's `workingHours` JSON from the form (seven days, like `operating_hours`). */
export function workingHoursInput(hours: WeeklyHoursForm): Record<Weekday, object> {
  const out = {} as Record<Weekday, object>;
  for (const day of WEEKDAYS) {
    const h = hours[day];
    out[day] = { available: h.available, alwaysAvailable: false, start: h.start, end: h.end };
  }
  return out;
}

function hoursProblem(hours: WeeklyHoursForm): string | undefined {
  if (!WEEKDAYS.some(day => hours[day].available)) return 'Open at least one day';
  for (const day of WEEKDAYS) {
    const h = hours[day];
    if (!h.available) continue;
    if (!TIME.test(h.start) || !TIME.test(h.end))
      return `${WEEKDAY_LABELS[day]}: use times like 09:00`;
    if (h.start >= h.end) return `${WEEKDAY_LABELS[day]}: close after you open`;
  }
  return undefined;
}

/**
 * The `updateWorker` input of the given fields, or the problems to show next
 * to the boxes. Fields not listed keep their saved value on the API.
 */
export function toWorkerSettingsInput(
  form: WorkerForm,
  keys: readonly WorkerFormKey[],
): { input: IWorkerSettingsInput; errors: WorkerFormErrors } {
  const input: IWorkerSettingsInput = {};
  const errors: WorkerFormErrors = {};
  const has = (key: WorkerFormKey) => keys.includes(key);

  if (has('displayName')) {
    const name = form.displayName.trim();
    if (!name || name.length > 60) errors.displayName = 'Use 1 to 60 characters';
    else input.displayName = name;
  }
  if (has('bio')) {
    if (form.bio.length > 1000) errors.bio = 'Keep it under 1,000 characters';
    else input.bio = form.bio.trim();
  }
  if (has('yearsExperience') && form.yearsExperience.trim()) {
    const years = wholeNumber(form.yearsExperience, 0, 60, 'Enter 0 to 60 years');
    if (years.error) errors.yearsExperience = years.error;
    else input.yearsExperience = years.value;
  }
  if (has('languages')) {
    input.languages = form.languages
      .split(',')
      .map(l => l.trim())
      .filter(Boolean);
  }

  // Spec 028: limits and words in the worker's unit (D6); the unit goes with the numbers.
  const unit = form.distanceUnit ?? 'mi';
  const limits = SERVICE_TRAVEL_LIMITS[unit];
  const word = distanceWord(unit);
  if (has('serviceRadiusMiles') || (has('acceptsOutOfRadius') && form.acceptsOutOfRadius)) {
    input.distanceUnit = unit;
  }
  let radius: number | null = null;
  if (has('serviceRadiusMiles')) {
    const parsed = wholeNumber(
      form.serviceRadiusMiles,
      1,
      limits.maxRadius,
      `Enter 1 to ${limits.maxRadius} ${word}`,
    );
    if (parsed.error) errors.serviceRadiusMiles = parsed.error;
    else input.serviceRadiusMiles = radius = parsed.value;
  }
  if (has('serviceZips')) {
    const { zips, invalid } = parseZipList(form.serviceZips);
    if (invalid.length) {
      errors.serviceZips = `Not a 5-digit ZIP code or postal area: ${invalid.join(', ')}`;
    } else if (zips.length > 50) errors.serviceZips = 'List up to 50 ZIP codes';
    else input.serviceZips = zips;
  }
  if (has('acceptsOutOfRadius')) {
    input.acceptsOutOfRadius = form.acceptsOutOfRadius;
    if (form.acceptsOutOfRadius) {
      const perMile = Number(form.perMileBeyondRadius);
      if (
        !form.perMileBeyondRadius.trim() ||
        !Number.isFinite(perMile) ||
        perMile < 0 ||
        perMile > limits.maxPerUnit
      ) {
        errors.perMileBeyondRadius = `Enter a price per ${distanceWord(unit, false)} from $0 to $${limits.maxPerUnit}`;
      } else {
        input.perMileBeyondRadius = Math.round(perMile * 100) / 100;
      }
      const farthest = wholeNumber(
        form.maxTravelMiles,
        1,
        limits.maxTravel,
        `Enter 1 to ${limits.maxTravel} ${word}`,
      );
      if (farthest.error) errors.maxTravelMiles = farthest.error;
      else if (radius !== null && farthest.value! < radius) {
        errors.maxTravelMiles = 'The farthest distance must be at least the service radius';
      } else input.maxTravelMiles = farthest.value;
    }
  }

  if (has('useLocationHours')) {
    if (form.useLocationHours) {
      input.workingHours = null;
    } else {
      const problem = hoursProblem(form.workingHours);
      if (problem) errors.workingHours = problem;
      else input.workingHours = workingHoursInput(form.workingHours);
    }
  }
  if (has('bufferHours')) {
    const buffer = wholeNumber(form.bufferHours, 0, 6, 'Enter 0 to 6 hours');
    if (buffer.error) errors.bufferHours = buffer.error;
    else input.bufferHours = buffer.value;
  }
  if (has('advanceNoticeHours')) {
    const notice = wholeNumber(form.advanceNoticeHours, 1, 72, 'Enter 1 to 72 hours');
    if (notice.error) errors.advanceNoticeHours = notice.error;
    else input.advanceNoticeHours = notice.value;
  }

  if (has('instantBook')) input.instantBook = form.instantBook;
  if (has('confirmWindowHours') && !form.instantBook) {
    const window = wholeNumber(form.confirmWindowHours, 1, 24, 'Enter 1 to 24 hours');
    if (window.error) errors.confirmWindowHours = window.error;
    else input.confirmWindowHours = window.value;
  }
  if (has('supplies')) input.suppliesProvided = { ...form.supplies };

  return { input, errors };
}

// ─── wizard ─────────────────────────────────────────────────────────────────

export type WizardStepId =
  | 'profile'
  | 'services'
  | 'area'
  | 'schedule'
  | 'equipment'
  | 'credentials'
  | 'booking';

export interface WizardStep {
  id: WizardStepId;
  title: string;
  description: string;
  /** The settings saved by this step's Next; other steps save through their own mutation. */
  section?: WorkerSection;
}

export const WIZARD_STEPS: readonly WizardStep[] = [
  {
    id: 'profile',
    title: 'Name and bio',
    description: 'Customers see this name and bio on the worker card.',
    section: 'profile',
  },
  {
    id: 'services',
    title: 'Services and prices',
    description:
      'Pick the services this worker does. Prices start at our suggested prices; change any of them.',
  },
  {
    id: 'area',
    title: 'Service area',
    description:
      'How far this worker goes. Distance is measured from the location; customers see only the distance.',
    section: 'area',
  },
  {
    id: 'schedule',
    title: 'Hours, buffer and notice',
    description: 'When this worker can be booked, and the gap they need between jobs.',
    section: 'schedule',
  },
  {
    id: 'equipment',
    title: 'Equipment and supplies',
    description: 'Show the tools this worker brings, and say which supplies they bring.',
    section: 'supplies',
  },
  {
    id: 'credentials',
    title: 'Insurance and background check',
    description:
      'Both are optional badges. A handover needs a verified background check for this worker.',
  },
  {
    id: 'booking',
    title: 'How customers book',
    description: 'Let customers book right away, or confirm each request yourself.',
    section: 'booking',
  },
];

export const wizardStepIndex = (id: string | null | undefined) =>
  WIZARD_STEPS.findIndex(step => step.id === id);

export function nextWizardStep(id: WizardStepId): WizardStepId | null {
  const index = wizardStepIndex(id);
  return index >= 0 && index < WIZARD_STEPS.length - 1 ? WIZARD_STEPS[index + 1].id : null;
}

export function previousWizardStep(id: WizardStepId): WizardStepId | null {
  const index = wizardStepIndex(id);
  return index > 0 ? WIZARD_STEPS[index - 1].id : null;
}

/** Where a draft opens: the step it reached, else the first one. */
export function resumeWizardStep(saved: string | null | undefined): WizardStepId {
  return wizardStepIndex(saved) >= 0 ? (saved as WizardStepId) : 'profile';
}

/** Which wizard step fixes each publish problem code (backend `workerPublishProblems`). */
export const PROBLEM_STEP: Record<string, WizardStepId> = {
  display_name_missing: 'profile',
  no_active_offering: 'services',
  service_radius: 'area',
  max_travel_miles: 'area',
  no_working_hours: 'schedule',
  equipment_photos: 'equipment',
  supplies_not_set: 'equipment',
  handover_background_check: 'credentials',
};

/** Problems fixed on another page. */
export const PROBLEM_LINK: Record<string, { to: string; label: string }> = {
  tax_info_missing: { to: PROVIDER_TAX_INFO_PATH, label: 'Add tax info' },
  provider_not_approved: { to: '/profile', label: 'See your application' },
};

export type ProblemAction =
  | { kind: 'step'; step: WizardStepId }
  | { kind: 'link'; to: string; label: string }
  | { kind: 'none' };

export function problemAction(code: string): ProblemAction {
  if (PROBLEM_STEP[code]) return { kind: 'step', step: PROBLEM_STEP[code] };
  if (PROBLEM_LINK[code]) return { kind: 'link', ...PROBLEM_LINK[code] };
  return { kind: 'none' };
}

// ─── status ─────────────────────────────────────────────────────────────────

export const LISTING_STATUS_LABELS: Record<ServiceListingStatus, string> = {
  [ServiceListingStatus.Draft]: 'Draft',
  [ServiceListingStatus.InProgress]: 'In progress',
  [ServiceListingStatus.Listed]: 'Listed',
  [ServiceListingStatus.Unlisted]: 'Unlisted',
};

export const isDraftStatus = (status: ServiceListingStatus) =>
  status === ServiceListingStatus.Draft || status === ServiceListingStatus.InProgress;

/** "92% on time", or a note while there are fewer than 5 jobs (D17). */
export function onTimeText(rate: number | null | undefined, completedJobs: number): string {
  if (rate === null || rate === undefined) {
    return completedJobs > 0 ? 'On-time rate after 5 jobs' : 'No jobs yet';
  }
  // The API sends a share from 0 to 1.
  return `${Math.round(rate * 100)}% on time`;
}

export function percentText(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return 'Not yet';
  return `${Math.round(rate * 100)}%`;
}
